import { createFileRoute } from '@tanstack/react-router'
import {
  UserRound,
  TrendingUp,
  Heart,
  Mail,
  Smartphone,
  FileWarning,
  ArrowRight,
} from 'lucide-react'
import { PLATFORM_CAPABILITIES } from '@/components/umbrella/capabilities'
import { motion } from 'framer-motion'
import {
  Container,
  SectionLabel,
  SectionHeading,
  FeatureCard,
  CtaBand,
  PrimaryButton,
  SecondaryButton,
  fadeUp,
  ease,
} from '@/components/umbrella/ui'

export const Route = createFileRoute('/solutions/financial-institutions')({
  component: FinancialInstitutionsPage,
})

/** Common scams your account holders face. */
const SCAMS = [
  {
    Icon: UserRound,
    title: 'Bank and government imposters',
    body: 'A caller posing as your fraud team, the IRS, or a utility claims an account is compromised and pressures the holder to "secure" their money by moving it.',
  },
  {
    Icon: TrendingUp,
    title: 'Investment and crypto fraud',
    body: 'Fake platforms and "advisors" promise high, low-risk returns, then block withdrawals once funds are in. Now the largest category of reported losses.',
  },
  {
    Icon: Heart,
    title: 'Romance scams',
    body: 'A long-con relationship built online ends in requests for money, often routed through the victim as a money mule without their knowledge.',
  },
  {
    Icon: Mail,
    title: 'Business email compromise',
    body: 'For business customers, a spoofed email redirects an invoice or payroll payment to a fraudulent account, often timed to a real transaction.',
  },
  {
    Icon: Smartphone,
    title: 'Payment app and wire fraud',
    body: 'Members are told to send themselves money or wire funds to "reverse" a fake charge, exploiting the speed and finality of instant payments.',
  },
  {
    Icon: FileWarning,
    title: 'Check and mobile-deposit scams',
    body: 'A too-good payment, overpayment, or fake job deposits a bad check and asks the holder to send part of it back before the check bounces.',
  },
]

/** Why this matters to the institution's balance sheet, not just its members. */
const BUSINESS_CASE = [
  {
    stat: '~70%',
    title: 'of U.S. household wealth is held by people 55+',
    body: 'Your most valuable relationships are also the most heavily targeted by scammers. Protecting them is deposit retention, not just goodwill.',
  },
  {
    stat: '$0',
    title: 'flagged by fraud systems when the member authorizes the payment',
    body: 'Most scam losses never look like fraud to transaction monitoring, because the member makes the transfer themselves. The only defense that works there is a member who recognizes the scam.',
  },
  {
    stat: '80%',
    title: 'less likely to engage with a scam they already understand',
    body: 'Consumer fraud research shows people who know how a scam works before it reaches them are up to 80% less likely to engage with it. Education moves the numbers monitoring cannot touch.',
  },
]

/** How a deployment comes together. */
const STEPS = [
  {
    title: 'Configure your resource',
    body: 'Choose the deployment model, brand treatment, priority scam topics, and links your account holders need.',
  },
  {
    title: 'Share through trusted channels',
    body: 'Promote through your website, fraud center, newsletters, branches, social channels, and member communications.',
  },
  {
    title: 'Measure and refresh',
    body: 'Use aggregate engagement insights and updated scam guidance to keep education useful as fraud tactics evolve.',
  },
]

function FinancialInstitutionsHero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-100">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_80%_20%,rgba(37,99,235,0.07),transparent_60%)]" />
      <Container className="grid items-center gap-12 py-20 lg:grid-cols-2 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
        >
          <SectionLabel>For Banks &amp; Credit Unions</SectionLabel>
          <h1 className="mt-4 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
            Protect your account holders from fraud{' '}
            <span className="text-blue-600">before the transfer clears.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
            Give your account holders fraud education branded as your own,
            built to help them spot scam tactics, verify suspicious requests,
            and pause before money leaves the account.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <PrimaryButton label="Talk to our team" href="#contact" />
            <SecondaryButton label="See the platform" href="/platform" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease, delay: 0.1 }}
        >
          <img
            src="/sidebyside.png"
            alt="A bank-branded learning experience on desktop and mobile"
            className="h-auto w-full"
            width={691}
            height={298}
          />
        </motion.div>
      </Container>
    </section>
  )
}

function FinancialInstitutionsPage() {
  return (
    <div className="bg-white">
      <FinancialInstitutionsHero />

      {/* Business case */}
      <section className="bg-slate-50 py-20">
      <Container>
        <SectionHeading
          label="The business case"
          title="Prevention is the part of fraud strategy your systems can't cover"
          blurb="Fraud detection watches transactions. Scams are won or lost earlier, in the conversation that convinces your member to authorize the payment."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {BUSINESS_CASE.map((c, i) => (
            <motion.div
              key={c.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-2xl border border-slate-200/80 bg-white p-7"
            >
              <p className="text-4xl font-semibold tracking-tight text-blue-600">{c.stat}</p>
              <h3 className="mt-3 text-lg font-semibold text-slate-900">{c.title}</h3>
              <p className="mt-2.5 text-base leading-relaxed text-slate-600">{c.body}</p>
            </motion.div>
          ))}
        </div>
        <motion.p {...fadeUp} className="mx-auto mt-10 max-w-3xl text-center text-lg leading-relaxed text-slate-600">
          And when a member spots a scam themselves because your institution
          taught them, they tell that story to their family with your name in
          it. That is retention no fraud alert ever earns.
        </motion.p>
        <p className="mt-8 text-sm leading-relaxed text-slate-500">
          Wealth share reflects Federal Reserve household data; awareness impact
          reflects consumer fraud research from the BBB Institute and FINRA
          Investor Education Foundation.
        </p>
      </Container>
      </section>

      {/* Why the institution */}
      <Container className="py-20">
        <SectionHeading
          label="What's included"
          title="A complete fraud-prevention resource, in your name"
          blurb="You already have the trust, reach, and channels your account holders rely on. ScamDefense gives you the education and tools to match, ready to deliver in your institution's brand."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PLATFORM_CAPABILITIES.map(c => (
            <FeatureCard key={c.title} {...c} />
          ))}
        </div>
      </Container>

      {/* Scams */}
      <section className="bg-slate-50 py-20">
        <Container>
          <SectionHeading
            label="Threats your account holders face"
            title="The scams that drain accounts"
            blurb="These are the patterns that most often end in a real loss. Education covers all of them, in plain language."
          />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SCAMS.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-slate-200/80 bg-white p-6"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <s.Icon className="h-4 w-4" />
                </span>
                <h3 className="mt-4 text-[15px] font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-xs leading-relaxed text-slate-500">
            Scam categories and loss rankings reflect FTC Consumer Sentinel
            Network and FBI IC3 reporting.
          </p>
        </Container>
      </section>

      {/* How it works */}
      <Container className="py-20">
        <SectionHeading
          label="How it works"
          title="Launch once. Keep improving."
          blurb="ScamDefense is a reusable platform layer, tailored to your audience, your campaigns, and the way you reach members."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-2xl border border-slate-200/80 bg-white p-7"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">
                {i + 1}
              </span>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed text-slate-600">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Container>

      {/* Military / defense credit union variant */}
      <section className="bg-slate-50 py-16">
        <Container>
          <motion.a
            href="/solutions/defense-credit-unions"
            {...fadeUp}
            className="group flex flex-col items-start justify-between gap-5 rounded-3xl border border-slate-200/80 bg-white p-8 transition hover:border-slate-300 hover:shadow-sm sm:flex-row sm:items-center"
          >
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-blue-600">Serve a military community?</p>
              <h3 className="mt-2 text-xl font-semibold text-slate-900">
                We have a program built for defense credit unions
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
                Tailored to the scams that target active duty members, military
                families, and veterans, from PCS-move fraud to VA benefit
                imposters.
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition group-hover:bg-slate-800">
              See the defense program
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </motion.a>
        </Container>
      </section>

      <CtaBand
        title="Bring fraud prevention to your account holders."
        blurb="Let’s talk about a member-branded ScamDefense deployment for your bank or credit union."
      />
    </div>
  )
}
